import React, { FC, ReactElement, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';

import { RootState } from 'store/rootReducer';
import { clearUpphovsorganisationer, getUpphovsorganisationer } from 'store/upphovsmanOrganisationerSlice';
import { clearUpphovspersoner, getUpphovspersoner } from 'store/upphovsmanPersonerSlice';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';
import { FlatUpphovsman, useUpphovsman } from 'hooks/useUpphovsman';

import { RaaAutocomplete, RaaDialog, RaaSelect } from 'raa-react';

import RaaInputField from 'components/fieldComponents/raaInputField/RaaInputField';
import { TextButton } from 'components/sharedElements/sharedElements';
import TagList from 'components/tagList/TagList';
import UpphovsmanInfoDialog from 'components/utkast/upphovsmanInfoDialog/UpphovsmanInfoDialog';
import { HandelseFormValues } from 'components/utkast/utkastRegistration/UtkastFormSchema';

import { UpphovsmanPut, UpphovsmanRollTyp, UpphovsmanTyp, Upphovsorganisation, Upphovsperson } from 'models';

import { UpphovsmanTypEnum } from 'enums';

import { ErrorMessage, FormikErrors, FormikTouched } from 'formik';
import { sanitizeElementId } from 'utilities/formUtilities';
import { isUpphovsorganisation, isUpphovsperson } from 'utilities/typeGuards';

interface HandelseFormProps {
  name?: string;
  labelText?: string;
  handelseFormValue: HandelseFormValues | undefined;
  errors: FormikErrors<HandelseFormValues>;
  touched: FormikTouched<HandelseFormValues>;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  selectedUpphovsman: UpphovsmanTyp[];
  newUpphovsmanSelected: (upphovsmanTyp: UpphovsmanTyp) => void;
}

const HandelseForm: FC<HandelseFormProps> = ({
  selectedUpphovsman,
  newUpphovsmanSelected,
  handelseFormValue,
  errors,
  touched,
  setFieldValue,
  name,
  labelText
}): ReactElement => {
  const upphovsmanrolltyper = useAppSelector(
    (state: RootState) => state.bebyggelseobjektDomaner.data?.upphovsmanrolltyper
  );
  const domaner = useAppSelector((state: RootState) => state.bebyggelseobjektDomaner.data);
  const { data: organisationerSummary, requestStatus: organisationerRequestStatus } = useAppSelector(
    (state: RootState) => state.upphovsmanOrganisationer
  );
  const { data: personerSummary, requestStatus: personerRequestStatus } = useAppSelector(
    (state: RootState) => state.upphovsmanPersoner
  );

  const delayDebounceFn = useRef<number>();
  const dispatch = useAppDispatch();
  const { setDialog, unsetDialog } = useDialog();

  const [searchQuery, setSearchQuery] = useState<string>('');

  const [upphovsmanOptions, setUpphovsmanOptions] = useState<Array<Upphovsperson | Upphovsorganisation>>([]);

  const [selectedUpphovsmantyp, setSelectedUpphovsmantyp] = useState<UpphovsmanTypEnum>(
    UpphovsmanTypEnum.UPPHOVSPERSON
  );

  const [selectedUpphovsmanRollTyp, setSelectedUpphovsmanRollTyp] = useState<UpphovsmanRollTyp>();

  const [tagList] = useUpphovsman(handelseFormValue?.upphovsman, selectedUpphovsman, domaner);

  const resetUpphovsmanrolltypSelect = useCallback(() => {
    if (upphovsmanrolltyper) {
      const firstRollBySortingOrder = upphovsmanrolltyper.reduce((min, current) =>
        current.sorteringsordning < min.sorteringsordning ? current : min
      );
      setSelectedUpphovsmanRollTyp(firstRollBySortingOrder);
    }
  }, [upphovsmanrolltyper]);

  const fetchUpphovsman = useCallback(
    (query: string, page: number, pageSize: number) => {
      if (selectedUpphovsmantyp === UpphovsmanTypEnum.UPPHOVSPERSON) {
        dispatch(getUpphovspersoner({ query, page, pageSize }));
      } else if (selectedUpphovsmantyp === UpphovsmanTypEnum.UPPHOVSORGANISATION) {
        dispatch(getUpphovsorganisationer({ query, page, pageSize }));
      }
    },
    [dispatch, selectedUpphovsmantyp]
  );

  const clearSelectedUpphovsmanOptions = useCallback(() => {
    dispatch(clearUpphovspersoner());
    dispatch(clearUpphovsorganisationer());
  }, [dispatch]);

  useEffect(() => {
    setUpphovsmanOptions([]);

    if (personerSummary && personerSummary.results.length) {
      setUpphovsmanOptions(personerSummary.results);
    }

    if (organisationerSummary && organisationerSummary.results.length) {
      setUpphovsmanOptions(organisationerSummary.results);
    }
  }, [personerSummary, organisationerSummary]);

  useEffect(() => {
    clearTimeout(delayDebounceFn.current);

    if (searchQuery.length > 2) {
      delayDebounceFn.current = window.setTimeout(() => {
        fetchUpphovsman(searchQuery, 0, 50);
      }, 500);
    } else {
      clearSelectedUpphovsmanOptions();
    }
  }, [dispatch, searchQuery, fetchUpphovsman, clearSelectedUpphovsmanOptions]);

  useEffect(() => {
    if (upphovsmanrolltyper) {
      resetUpphovsmanrolltypSelect();
    }
  }, [upphovsmanrolltyper, resetUpphovsmanrolltypSelect]);

  const fetchMoreUpphovsmanOnBottomScroll = () => {
    if (
      selectedUpphovsmantyp === UpphovsmanTypEnum.UPPHOVSPERSON &&
      personerSummary &&
      personerSummary.results.length < personerSummary.total_size
    ) {
      fetchUpphovsman(searchQuery, personerSummary.page + 1, 50);
    } else if (
      selectedUpphovsmantyp === UpphovsmanTypEnum.UPPHOVSORGANISATION &&
      organisationerSummary &&
      organisationerSummary.results.length < organisationerSummary.total_size
    ) {
      fetchUpphovsman(searchQuery, organisationerSummary.page + 1, 50);
    }
  };

  const addFieldUpphovsman = (_event: SyntheticEvent, upphovsman: UpphovsmanTyp | string) => {
    if (!upphovsman || typeof upphovsman === 'string' || !handelseFormValue) return;

    const upphovsmanFormValue = handelseFormValue.upphovsman;
    const foundUpphovsman = upphovsmanFormValue.find(
      (upphovsmanPut: UpphovsmanPut) => upphovsmanPut.id === upphovsman.upphovsman_id
    );

    if (foundUpphovsman && selectedUpphovsmanRollTyp) {
      foundUpphovsman.roller = Array.from(
        new Set(foundUpphovsman.roller).add(selectedUpphovsmanRollTyp?.upphovsmanrolltyp)
      );
      setFieldValue(name ? `${name}.upphovsman` : 'upphovsman', [...upphovsmanFormValue]);
    } else if (selectedUpphovsmanRollTyp) {
      const newUpphovsman: UpphovsmanPut = {
        roller: [selectedUpphovsmanRollTyp.upphovsmanrolltyp],
        id: upphovsman.upphovsman_id
      };

      newUpphovsmanSelected(upphovsman);
      setFieldValue(name ? `${name}.upphovsman` : 'upphovsman', [...upphovsmanFormValue, newUpphovsman]);
    }

    setSearchQuery('');
    resetUpphovsmanrolltypSelect();
  };

  const removeFieldUpphovsman = (flatUpphovsman: FlatUpphovsman): void => {
    if (!handelseFormValue) {
      return;
    }

    let upphovsman = handelseFormValue.upphovsman;

    const foundUpphovsman = upphovsman.find(
      (upphovsmanPut: UpphovsmanPut) => upphovsmanPut.id === flatUpphovsman.upphovsman.upphovsman_id
    );

    if (foundUpphovsman) {
      foundUpphovsman.roller = foundUpphovsman.roller.filter(
        (roll: string) => roll !== flatUpphovsman.role.upphovsmanrolltyp
      );
      if (foundUpphovsman.roller.length > 0) {
        setFieldValue(name ? `${name}.upphovsman` : 'upphovsman', [...upphovsman]);
      } else {
        upphovsman = upphovsman.filter(
          (upphovsmanPut: UpphovsmanPut) => upphovsmanPut.id !== flatUpphovsman.upphovsman.upphovsman_id
        );
        setFieldValue(name ? `${name}.upphovsman` : 'upphovsman', [...upphovsman]);
      }
    }
  };

  const onClickRemoveUpphovsmanTag = (removedItem: FlatUpphovsman) => {
    if (!removedItem.role.aktiv) {
      setDialog(RaaDialog, {
        header: 'Ta bort utfasat värde?',
        ariaDescribedBy: 'dialog-description',
        children: (
          <p id="dialog-description" className="tw-my-4">
            Du kommer inte att kunna välja den här upphovsrollen igen.
          </p>
        ),
        cancel: () => unsetDialog(),
        cancelButtonText: 'Avbryt',
        proceedButtonText: 'Ta bort',
        proceed: () => {
          removeFieldUpphovsman(removedItem);
          unsetDialog();
        }
      });
    } else {
      removeFieldUpphovsman(removedItem);
    }
  };

  const extractDateringError = (
    touched: FormikTouched<HandelseFormValues>,
    errors: FormikErrors<HandelseFormValues>
  ): string => {
    if (errors) {
      const touchedDateringTom = touched && touched.datering_tom;
      const touchedDateringFrom = touched && touched.datering_from;
      return (touchedDateringTom && errors?.datering_tom) || (touchedDateringFrom && errors?.datering_from) || '';
    }
    return '';
  };

  const handleInputChange = (event: SyntheticEvent, value: string) => {
    event.preventDefault();
    setSearchQuery(value);
  };

  const appendName = useCallback((suffix: string) => (name ? `${name}.${suffix}` : suffix), [name]);
  const appendNameAsId = useCallback((suffix: string) => sanitizeElementId(appendName(suffix)), [appendName]);

  return (
    <>
      {handelseFormValue && (
        <>
          <label htmlFor={appendNameAsId('datering_from')} className="raa-label tw-mx-2">
            {labelText}
          </label>
          <div className="tw-flex tw-flex-wrap">
            <div className="tw-ml-2 tw-mr-6">
              <div className="tw-flex tw-items-center">
                <label htmlFor={appendNameAsId('datering_kand_true')} className="tw-sr-only">
                  årtal
                </label>
                <RaaInputField
                  classString=" " // Disables default style
                  type="radio"
                  value="true"
                  id={appendNameAsId('datering_kand_true')}
                  name={appendName('datering_kand')}
                  checked={handelseFormValue.datering_kand === 'true'}
                />
                <RaaInputField
                  disabled={handelseFormValue.datering_kand === 'false'}
                  name={appendName('datering_from')}
                  maxLength={4}
                  classString="raa-input tw-w-16 tw-ml-2"
                  placeholder="ÅÅÅÅ"
                  showError={false}
                  shouldValidateForm={true}
                />
                <label htmlFor={appendNameAsId('datering_tom')} className="tw-mx-2">
                  <span className="tw-sr-only">{'Färdigställt '}</span>till
                </label>
                <RaaInputField
                  id={appendNameAsId('datering_tom')}
                  disabled={handelseFormValue.datering_kand === 'false'}
                  name={appendName('datering_tom')}
                  classString="raa-input tw-w-16"
                  maxLength={4}
                  placeholder="ÅÅÅÅ"
                  showError={false}
                  shouldValidateForm={true}
                />
              </div>
            </div>
            <div className="tw-mx-2 tw-flex tw-items-center">
              <RaaInputField
                classString=" " // Disables default style
                id={appendNameAsId('datering_kand_false')}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (event.currentTarget.value === 'false') {
                    setFieldValue(appendName('datering_tom'), '');
                    setFieldValue(appendName('datering_from'), '');
                  }
                  setFieldValue(appendName('datering_kand'), event.currentTarget.value, true);
                }}
                value="false"
                type="radio"
                checked={handelseFormValue.datering_kand === 'false'}
                name={appendName('datering_kand')}
              />
              <label htmlFor={appendNameAsId('datering_kand_false')} className="tw-ml-2">
                Okänt årtal
              </label>
            </div>
          </div>
          {extractDateringError(touched, errors) ? (
            <p className="tw-mx-2 tw-text-raa-error-red">{extractDateringError(touched, errors)}</p>
          ) : (
            <p className="tw-mx-8 tw-text-raa-gray-5">Använd två årtal för att ange osäkerhet</p>
          )}
          <div className="tw-mt-4 tw-flex tw-flex-wrap">
            {upphovsmanrolltyper && (
              <div className="tw-mx-2 tw-mb-4">
                <label className="raa-label" htmlFor={appendNameAsId('upphovsroll')}>
                  Upphov: Roll
                </label>
                <RaaSelect
                  inputElementId={appendNameAsId('upphovsroll')}
                  value={selectedUpphovsmanRollTyp}
                  domain={upphovsmanrolltyper.filter(
                    (rollTyp) =>
                      rollTyp.aktiv || rollTyp.upphovsmanrolltyp === selectedUpphovsmanRollTyp?.upphovsmanrolltyp
                  )}
                  displayAttr="namn"
                  onSelect={setSelectedUpphovsmanRollTyp}
                />
              </div>
            )}
            <div className="tw-mx-2 tw-mb-4 tw-min-w-[14rem] tw-grow">
              <div className="tw-flex">
                <label className="raa-label tw-mr-4" htmlFor={appendNameAsId('upphovsman')}>
                  Upphov:
                </label>

                <fieldset className="tw-flex tw-items-center tw-gap-4">
                  <legend className="tw-sr-only">Upphov</legend>

                  <label className="raa-label" htmlFor="upphovsman-person">
                    <input
                      type="radio"
                      id="upphovsman-person"
                      name="upphov"
                      className="tw-mr-2"
                      checked={selectedUpphovsmantyp === UpphovsmanTypEnum.UPPHOVSPERSON}
                      onChange={() => {
                        setSearchQuery('');
                        setSelectedUpphovsmantyp(UpphovsmanTypEnum.UPPHOVSPERSON);
                      }}
                    />
                    Person
                  </label>

                  <label className="raa-label tw-px-2" htmlFor="upphovsman-organisation">
                    <input
                      type="radio"
                      id="upphovsman-organisation"
                      name="upphov"
                      className="tw-mr-2"
                      checked={selectedUpphovsmantyp === UpphovsmanTypEnum.UPPHOVSORGANISATION}
                      onChange={() => {
                        setSearchQuery('');
                        setSelectedUpphovsmantyp(UpphovsmanTypEnum.UPPHOVSORGANISATION);
                      }}
                    />
                    Organisation
                  </label>
                </fieldset>
              </div>

              <RaaAutocomplete
                id={appendNameAsId('upphovsman')}
                noOptionsText="Inget resultat"
                options={upphovsmanOptions}
                value={searchQuery}
                displayValues={
                  selectedUpphovsmantyp === UpphovsmanTypEnum.UPPHOVSPERSON
                    ? [['fornamn', 'efternamn', ['f.(', 'fodelse_ar', ')']], ['identifieringsstod']]
                    : [['upphovsorganisationsnamn']]
                }
                onChange={addFieldUpphovsman}
                onInputChange={handleInputChange}
                pending={
                  selectedUpphovsmantyp === UpphovsmanTypEnum.UPPHOVSPERSON
                    ? personerRequestStatus === 'pending'
                    : organisationerRequestStatus === 'pending'
                }
                placeholder="- Skriv namn -"
                optionKey="upphovsman_id"
                isOptionEqualToValue={(
                  option: Upphovsperson | Upphovsorganisation,
                  value: Upphovsperson | Upphovsorganisation
                ) => option.upphovsman_id === value.upphovsman_id}
                getOptionLabel={(option: Upphovsperson | Upphovsorganisation | string) => {
                  if (typeof option === 'string') return option;

                  if (isUpphovsperson(option)) {
                    return `${(option as Upphovsperson).fornamn} ${(option as Upphovsperson).efternamn}`;
                  }

                  if (isUpphovsorganisation(option)) {
                    return `${(option as Upphovsorganisation).upphovsorganisationsnamn}`;
                  }

                  return '';
                }}
                onBottomScroll={fetchMoreUpphovsmanOnBottomScroll}
              />
            </div>
          </div>

          {tagList && (
            <div className="tw-mx-2">
              <TagList
                list={tagList}
                leftColumn={(tag) => (
                  <div className="tw-flex">
                    <span className="tw-whitespace-pre">{`${tag.role.namn}: `}</span>
                    <TextButton
                      onClick={() => setDialog(UpphovsmanInfoDialog, { unsetDialog, id: tag.upphovsman.upphovsman_id })}
                    >
                      {tag.displayName}
                    </TextButton>
                  </div>
                )}
                rightColumn={(item) => (!item.role.aktiv ? '(utfasad roll)' : '')}
                onRemoveItem={onClickRemoveUpphovsmanTag}
              />

              {!!errors && (
                <ErrorMessage name={name + '.upphovsman'} component="p" className="tw-mt-2 tw-text-raa-error-red" />
              )}
            </div>
          )}
          <div className="tw-mx-2">
            <RaaInputField label="Kommentar" name={appendName('kommentar')} placeholder="Kommentar om händelsen" />
          </div>
        </>
      )}
    </>
  );
};

export default HandelseForm;
